import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { UpdateAttachment } from '../../interfaces/update-attachment';
import { Feed } from '../../interfaces/feed';
import { Share } from '@capacitor/share';
import { CurrentWhiteLabelApplication } from 'src/app/utils/current-white-label-application';
import { CardType } from 'src/app/enums/card-type';
import { ApiService } from 'src/app/services/api.service';
import { Router, RouterLink } from '@angular/router';
import { Project } from 'src/app/interfaces/project';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { Update } from 'src/app/interfaces/update';
import { ContentTextPipe } from 'src/app/pipes/content-text.pipe';
import { UpdateLikeType } from 'src/app/enums/update-like-type';
import { EventsComponent } from '../events/events.component';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { ReadMorePipe } from '../../pipes/read-more.pipe';
import { TruncateModule } from '@yellowspot/ng-truncate';
import { PollComponent } from '../poll/poll.component';
import { SocialButtonsComponent } from '../social-button/social-buttons.component';
import { MediaSliderComponent } from '../media-slider/media-slider.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { TranslateTextDirective } from '../../directives/translate-text.directive';
import { CardComponent } from '../cards/card/card.component';
import { ImgFallBackDirective } from '../../directives/img-fallback.directive';
import { NgIf, DatePipe } from '@angular/common';
import { DivClickDirective } from 'src/app/directives/div-click.directive';

@Component({
  selector: 'app-feed-item',
  templateUrl: './feed-item.component.html',
  standalone: true,
  imports: [
    NgIf,
    ImgFallBackDirective,
    RouterLink,
    CardComponent,
    ContentTextPipe,
    TranslateTextDirective,
    InlineSVGModule,
    EventsComponent,
    MediaSliderComponent,
    SocialButtonsComponent,
    PollComponent,
    DatePipe,
    DivClickDirective,
    TranslateModule,
    TruncateModule,
    ReadMorePipe,
  ],
})
export class FeedItemComponent implements OnInit {
  item: Feed;
  CardType = CardType;
  appLanguage: string;
  whitelabelIcon: string;
  whitelabelName: string;
  whitelabelText: string = '';
  images: UpdateAttachment[];
  canShare: boolean = false;
  isProjectApp: boolean = false;

  @Input('item') set itemSetter(item: Feed) {
    this.item = item;
  }
  @Input() likedType: UpdateLikeType;
  @Output() deleted = new EventEmitter<number>();
  @Output() itemVisible = new EventEmitter<Feed>();
  @ViewChild(EventsComponent) eventsComponent!: EventsComponent;

  private observer: IntersectionObserver;
  private isVisibleTimer: any;

  constructor(
    private selfReference: ElementRef,
    private apiService: ApiService,
    private translateService: TranslateService,
    private router: Router,
    private elementRef: ElementRef,
    private app: CurrentWhiteLabelApplication,
    private analyticsService: AnalyticsService,
    private contentTextPipe: ContentTextPipe
  ) {
    Share.canShare().then(
      (result) => (this.canShare = result.value && !!app.getPwaDomain())
    );
    this.appLanguage = this.translateService.currentLang;
    this.whitelabelIcon = app.getIconThumbnails()?.small;
    this.whitelabelName = app.getName();
    this.whitelabelText = app.getCompanyText();
    this.isProjectApp = this.app.isProjectApp();
  }
  ngOnInit() {
    if (this.item.projectUpdate) {
      this.item.projectUpdate['contentText'] = this.contentTextPipe.transform(
        this.item.projectUpdate.content
      );
    }

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!this.item.projectUpdate) {
          return;
        }
        const entry = entries[0];
        if (entry.isIntersecting) {
          clearTimeout(this.isVisibleTimer);

          this.isVisibleTimer = setTimeout(() => {
            this.handleVisible();
          }, 2000);
        } else {
          clearTimeout(this.isVisibleTimer);
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.6,
      }
    );

    this.observer.observe(this.selfReference.nativeElement);
  }

  ngAfterViewInit() {
    if (
      this.isElementVisible(this.elementRef.nativeElement) &&
      this.item.projectUpdate
    ) {
      this.handleVisible();
    }

    const readMore: HTMLElement =
      this.elementRef.nativeElement.querySelector('a.read-more');
    if (readMore) {
      readMore.addEventListener('click', (event) => {
        event.preventDefault();
      });
    }
  }

  handleVisible() {
    this.itemVisible.emit(this.item);
  }

  isElementVisible(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const topShown = rect.top >= 0;
    const bottomShown = rect.bottom <= window.innerHeight;
    return topShown && bottomShown;
  }

  async share(event) {
    event.stopPropagation();
    let url = `https://${this.app.getPwaDomain()}`;
    if (this.item.type === CardType.UPDATE)
      url += `/projects/${this.item.project.slug}/update/${this.item.projectUpdate.id}`;
    else if (this.item.type === CardType.GENERAL)
      url += `/messages/${this.item.message.id}`;
    else return;

    await Share.share({
      url: url,
    }).catch((e) => {});

    if (this.item.type === CardType.UPDATE) {
      this.analyticsService.logEvent(
        AnalyticsType.UPDATE_SHARED,
        {
          update_id: this.item.projectUpdate.id,
        },
        this.item.project
      );
      return;
    }
  }

  toggleReadMore(update: Update) {
    if (update['expanded'] == undefined) {
      this.analyticsService.logEvent(
        AnalyticsType.UPDATE_OPEN,
        {
          update_id: update.id,
        },
        this.item.project
      );
    }
    update['expanded'] = true;
  }

  getTruncateLength(update: Update): number {
    if (update['expanded']) {
      return (
        update['translatedContent']?.length || update['contentText'].length
      );
    } else {
      return 300;
    }
  }

  removeFromFeed(id: number) {
    this.apiService.identifiedDelete('/api/v3/feed/' + id).subscribe();
    this.deleted.emit(id);
  }

  openSurvey(project: Project, surveyId: Number) {
    this.router.navigate(['projects', project.slug, 'survey', surveyId]);
  }

  openUpdateOrigin(item: Feed) {
    if (item.project !== null) {
      this.openProject(item.project);
    } else {
      this.router.navigate(
        ['organisations', item.projectUpdate.customer.slug],
        {
          state: { language: null },
        }
      );
    }
  }

  openProject(project: Project) {
    if (this.app.isProjectApp()) {
      this.router.navigate(['project']);
    } else {
      this.router.navigate(['projects', project.slug]);
    }
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
